import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { usePermissions } from "@/hooks/usePermissions";
import AdminLayout from "@/components/admin/AdminLayout";
import { TableShell, Th, Td, EmptyRow } from "@/components/admin/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import ImageUpload from "@/components/admin/ImageUpload";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

// fields: [{key,label,type,options?,span?}] type: text|number|textarea|switch|image
export default function CrudManager({ title, endpoint, module, columns, fields, defaults = {}, transform }) {
  const { can } = usePermissions();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaults);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(endpoint).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, [endpoint]);

  const openNew = () => { setEditing(null); setForm({ ...defaults }); setOpen(true); };
  const openEdit = (item) => { setEditing(item); setForm({ ...item }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let payload = { ...form };
      if (transform) payload = transform(payload);
      if (editing) await api.put(`${endpoint}/${editing.id}`, payload);
      else await api.post(endpoint, payload);
      toast.success(editing ? "Updated" : "Created");
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setBusy(false);
  };

  const remove = async (item) => {
    try { await api.delete(`${endpoint}/${item.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <AdminLayout title={title} actions={can(module, "create") && <Button size="sm" onClick={openNew} data-testid="crud-add-btn" className="rounded-full"><Plus className="mr-1 h-4 w-4" />Add</Button>}>
      <TableShell>
        <thead className="border-b border-border bg-secondary/40">
          <tr>{columns.map((c) => <Th key={c.key}>{c.label}</Th>)}<Th className="text-right">Actions</Th></tr>
        </thead>
        <tbody className="divide-y divide-border">
          {items.length === 0 ? <EmptyRow colSpan={columns.length + 1} /> : items.map((item) => (
            <tr key={item.id} className="hover:bg-secondary/30" data-testid={`row-${item.id}`}>
              {columns.map((c) => <Td key={c.key}>{c.render ? c.render(item) : String(item[c.key] ?? "—")}</Td>)}
              <Td className="text-right">
                <div className="flex justify-end gap-1">
                  {can(module, "edit") && <button onClick={() => openEdit(item)} data-testid={`edit-${item.id}`} className="grid h-8 w-8 place-items-center rounded-md hover:bg-secondary"><Pencil className="h-4 w-4" /></button>}
                  {can(module, "delete") && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild><button data-testid={`delete-${item.id}`} className="grid h-8 w-8 place-items-center rounded-md text-destructive hover:bg-destructive/10"><Trash2 className="h-4 w-4" /></button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Delete this record?</AlertDialogTitle><AlertDialogDescription>This action is logged in the audit trail.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => remove(item)} data-testid={`confirm-delete-${item.id}`}>Delete</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </Td>
            </tr>
          ))}
        </tbody>
      </TableShell>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} {title.replace(/s$/, "")}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="grid grid-cols-2 gap-4">
            {fields.map((f) => (
              <div key={f.key} className={f.span === 2 || f.type === "textarea" ? "col-span-2" : "col-span-1"}>
                <Label className="mb-1.5 block">{f.label}</Label>
                {f.type === "switch" ? (
                  <div className="flex h-10 items-center"><Switch checked={!!form[f.key]} onCheckedChange={(v) => setField(f.key, v)} data-testid={`field-${f.key}`} /></div>
                ) : f.type === "image" ? (
                  <ImageUpload value={form[f.key]} onChange={(v) => setField(f.key, v)} testid={`field-${f.key}`} />
                ) : f.type === "textarea" ? (
                  <Textarea value={form[f.key] || ""} onChange={(e) => setField(f.key, e.target.value)} data-testid={`field-${f.key}`} />
                ) : (
                  <Input type={f.type === "number" ? "number" : "text"} value={form[f.key] ?? ""} onChange={(e) => setField(f.key, f.type === "number" ? Number(e.target.value) : e.target.value)} data-testid={`field-${f.key}`} />
                )}
              </div>
            ))}
            <Button type="submit" disabled={busy} className="col-span-2 rounded-full" data-testid="crud-save-btn">{busy ? "Saving..." : "Save"}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
